import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import PromptInput from './components/PromptInput';
import CategoryChips from './components/CategoryChips';
import PromptResults from './components/PromptResults';
import GenerationStats from './components/GenerationStats';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const PromptGenerator = () => {
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(true); // Mock authentication
  const [userTier, setUserTier] = useState('launch'); // Mock user tier
  const [generationStats, setGenerationStats] = useState({
    totalGenerated: 23,
    todayGenerated: 3,
    savedPrompts: 8
  });

  // Mock prompt data
  const mockPrompts = [
    {
      id: 1,
      title: "Marketing Email Campaign",
      content: `Subject: Introducing Our Revolutionary New Product - Transform Your Workflow Today!\n\nDear [Customer Name],\n\nWe're thrilled to announce the launch of our groundbreaking new product that will revolutionize how you approach your daily tasks. After months of development and testing, we're confident this solution will exceed your expectations.\n\nKey Benefits:\n• Increase productivity by 40%\n• Streamline complex workflows\n• Reduce manual errors\n• Save 2+ hours daily\n\nAs a valued customer, you're eligible for an exclusive 25% early-bird discount. This limited-time offer expires in 7 days.\n\n[CTA Button: Claim Your Discount Now]\n\nQuestions? Reply to this email or call our support team at [phone number].\n\nBest regards,\n[Your Name]\n[Company Name]`,
      category: "marketing"
    },
    {
      id: 2,
      title: "Technical Documentation Template",
      content: `# API Integration Guide

## Overview
This document provides comprehensive instructions for integrating with our REST API endpoints.

## Authentication
- API Key required in header: 'Authorization: Bearer [your-api-key]'
- Rate limit: 1000 requests/hour

## Endpoints

### GET /api/v1/data
Retrieve data with optional filtering parameters.

**Parameters:**
- limit (integer): Number of records to return (max 100)
- offset (integer): Starting position for pagination
- filter (string): Search criteria

**Response:**
\`\`\`json
{
  "status": "success",
  "data": [...],
  "total": 150,
  "page": 1
}
\`\`\`

### POST /api/v1/data
Create new data entry.

**Request Body:**
\`\`\`json
{
  "name": "string",
  "value": "string",
  "metadata": {}
}
\`\`\`

## Error Handling
All errors return standard HTTP status codes with descriptive messages.`,
      category: "technical"
    },
    {
      id: 3,
      title: "Legal Contract Clause",
      content: `DATA PRIVACY AND PROTECTION CLAUSE

1. DATA COLLECTION AND USE
The Service Provider agrees to collect, process, and store personal data in accordance with applicable data protection laws, including but not limited to GDPR, CCPA, and other relevant privacy regulations.

2. DATA SECURITY MEASURES
The Service Provider shall implement appropriate technical and organizational measures to ensure a level of security appropriate to the risk, including:
a) Encryption of personal data in transit and at rest
b) Regular security assessments and penetration testing
c) Access controls and authentication mechanisms
d) Employee training on data protection practices

3. DATA BREACH NOTIFICATION
In the event of a personal data breach, the Service Provider shall notify the Client within 72 hours of becoming aware of the breach, providing:
- Nature of the breach
- Categories and approximate number of data subjects affected
- Likely consequences of the breach
- Measures taken or proposed to address the breach

4. DATA SUBJECT RIGHTS
The Service Provider shall assist the Client in responding to requests from data subjects exercising their rights under applicable data protection laws.

5. DATA RETENTION
Personal data shall be retained only for as long as necessary for the purposes specified in this agreement, unless longer retention is required by law.`,
      category: "legal"
    }
  ];

  // Check authentication on component mount
  useEffect(() => {
    const checkAuth = () => {
      // Mock authentication check
      const authStatus = localStorage.getItem('isAuthenticated') === 'true';
      if (!authStatus) {
        navigate('/user-authentication', { 
          state: { from: '/prompt-generator' } 
        });
      }
      setIsAuthenticated(authStatus);
    };

    checkAuth();
  }, [navigate]);

  const handleInputChange = (value) => {
    setInputValue(value);
  };

  const handleGenerate = async () => {
    if (!inputValue.trim()) return;

    setIsLoading(true);
    
    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Mock category classification
      const mockCategories = ['marketing', 'business', 'content'];
      setCategories(mockCategories);
      
      // Set mock prompts
      setPrompts(mockPrompts);
      
      // Update generation stats
      setGenerationStats(prev => ({
        ...prev,
        totalGenerated: prev.totalGenerated + 1,
        todayGenerated: prev.todayGenerated + 1
      }));
      
    } catch (error) {
      console.error('Error generating prompts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyPrompt = (prompt) => {
    console.log('Copied prompt:', prompt.title);
  };

  const handleSavePrompt = (prompt) => {
    if (userTier === 'free') {
      // Redirect to pricing for free users
      navigate('/pricing-plans');
      return;
    }
    
    setGenerationStats(prev => ({
      ...prev,
      savedPrompts: prev.savedPrompts + 1
    }));
    
    console.log('Saved prompt:', prompt.title);
  };

  const canSavePrompts = userTier !== 'free';

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
            <Icon name="Lock" size={32} color="var(--color-primary)" />
          </div>
          <h2 className="text-xl font-semibold text-text-primary">
            Authentication Required
          </h2>
          <p className="text-text-secondary">
            Please sign in to access the Prompt Generator
          </p>
          <Button
            variant="default"
            onClick={() => navigate('/user-authentication')}
            iconName="LogIn"
            iconPosition="left"
          >
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-6">
          {/* Page Header */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Sparkles" size={32} color="var(--color-primary)" />
            </div>
            <h1 className="text-4xl font-bold text-text-primary mb-4">
              AI Prompt Generator
            </h1>
            <p className="text-xl text-text-secondary max-w-3xl mx-auto">
              Transform your ideas into powerful, AI-optimized prompts across marketing, legal, technical, and creative domains
            </p>
          </div>

          {/* Generation Statistics */}
          <GenerationStats
            totalGenerated={generationStats.totalGenerated}
            todayGenerated={generationStats.todayGenerated}
            savedPrompts={generationStats.savedPrompts}
            userTier={userTier}
          />

          {/* Prompt Input Section */}
          <div className="mb-12">
            <PromptInput
              inputValue={inputValue}
              onInputChange={handleInputChange}
              onGenerate={handleGenerate}
              isLoading={isLoading}
            />
          </div>

          {/* Category Classification Results */}
          {categories.length > 0 && !isLoading && (
            <CategoryChips categories={categories} />
          )}

          {/* Prompt Results Section */}
          <PromptResults
            prompts={prompts}
            onCopyPrompt={handleCopyPrompt}
            onSavePrompt={handleSavePrompt}
            canSave={canSavePrompts}
            isLoading={isLoading}
          />

          {/* Upgrade CTA for Free Users */}
          {userTier === 'free' && prompts.length > 0 && (
            <div className="mt-12 text-center">
              <div className="bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/20 rounded-lg p-8 max-w-2xl mx-auto">
                <Icon name="Crown" size={32} color="var(--color-primary)" className="mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-text-primary mb-2">
                  Unlock Advanced Features
                </h3>
                <p className="text-text-secondary mb-6">
                  Save prompts, access categorized suggestions, and get unlimited generations with our premium plans
                </p>
                <Button
                  variant="default"
                  size="lg"
                  onClick={() => navigate('/pricing-plans')}
                  iconName="ArrowRight"
                  iconPosition="right"
                >
                  View Pricing Plans
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default PromptGenerator;